import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  shareReplay,
  startWith,
  take,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ApiService, PaxiNode } from '../../services/api.service';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'app-landing-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.scss',
})
export class LandingPageComponent {
  private readonly apiService = inject(ApiService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  protected readonly sendingStoreControl = new FormControl<PaxiNode | string | null>(null);
  protected readonly collectionStoreControl = new FormControl<PaxiNode | string | null>(null);
  protected availabilityMessage = '';
  protected nodeParamErrorMessage = '';
  protected loadingSendingStores = true;
  protected hasLoadedDestinations = false;
  protected hasAvailableCollectionStores = false;
  protected loadingDestinations = false;

  private readonly destinations$ = new BehaviorSubject<PaxiNode[] | null>(null);

  protected readonly mapLocations$: Observable<PaxiNode[]> = this.apiService
    .getNodeList()
    .pipe(
      tap(() => {
        this.loadingSendingStores = false;
      }),
      shareReplay(1),
    );

  protected readonly filteredSendingStores$: Observable<PaxiNode[]> = combineLatest([
    this.mapLocations$,
    this.sendingStoreControl.valueChanges.pipe(startWith(this.sendingStoreControl.value)),
  ]).pipe(map(([locations, query]) => this.filterLocations(locations, query)));

  protected readonly filteredCollectionStores$: Observable<PaxiNode[]> = combineLatest([
    this.destinations$,
    this.collectionStoreControl.valueChanges.pipe(startWith(this.collectionStoreControl.value)),
  ]).pipe(
    map(([destinations, query]) => this.filterLocations(destinations ?? [], query)),
  );

  constructor() {
    this.sendingStoreControl.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string' && value.trim().length > 0),
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe((query) => {
        this.analyticsService.logEvent('sending_store_text_search', { query });
      });

    this.collectionStoreControl.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string' && value.trim().length > 0),
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe((query) => {
        this.analyticsService.logEvent('collection_store_text_search', { query });
      });

    this.preselectSendingStoreFromQueryParam();
  }

  private preselectSendingStoreFromQueryParam(): void {
    const nodeCode = this.getNodeQueryParam();
    if (!nodeCode) {
      return;
    }

    const normalizedNodeCode = nodeCode.trim().toLowerCase();

    this.mapLocations$.pipe(take(1), takeUntilDestroyed()).subscribe((locations) => {
      const match = locations.find(
        (location) => location.nodeCode?.trim().toLowerCase() === normalizedNodeCode,
      );

      if (!match) {
        this.nodeParamErrorMessage = `Node ${nodeCode} is not eligible for express delivery`;
        this.analyticsService.logEvent('node_param_not_eligible', { node_code: nodeCode });
        this.changeDetectorRef.markForCheck();
        return;
      }

      this.selectSendingStore(match);
      this.changeDetectorRef.markForCheck();
    });
  }

  private getNodeQueryParam(): string | null {
    const routeParam = this.route.snapshot.queryParamMap.get('node');
    if (routeParam) {
      return routeParam;
    }

    // With the hash location strategy a param placed before the hash (e.g. /?node=P0137)
    // never reaches the router, so fall back to the raw browser query string.
    return new URLSearchParams(window.location.search).get('node');
  }

  protected onCheckAvailability(): void {
    const fromStore = this.sendingStoreControl.value;
    const toStore = this.collectionStoreControl.value;

    if (!this.isMapLocation(fromStore) || !this.isMapLocation(toStore)) {
      this.availabilityMessage = 'Please select both stores before checking availability.';
      return;
    }

    this.apiService
      .checkAvailability({ originNodeCode: fromStore.nodeCode, destinationNodeCode: toStore.nodeCode })
      .subscribe((response) => {
      this.availabilityMessage = response.valid ? 'This route is available for Express delivery.' : 'This route is not available for Express delivery.';
    });
  }

  protected onSendingStoreSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectSendingStore(event.option.value as PaxiNode);
  }

  private selectSendingStore(selectedStore: PaxiNode): void {
    this.nodeParamErrorMessage = '';
    this.sendingStoreControl.setValue(selectedStore);
    this.collectionStoreControl.setValue(null);
    this.hasLoadedDestinations = false;
    this.hasAvailableCollectionStores = false;
    this.loadingDestinations = true;

    this.analyticsService.logEvent('sending_store_selected', {
      node_code: selectedStore.nodeCode,
      node_name: selectedStore.nodeShortName,
    });

    this.apiService
      .getDestinations(selectedStore.nodeCode)
      .pipe(finalize(() => (this.loadingDestinations = false)))
      .subscribe((response) => {
        const destinations = response.destinations ?? [];
        console.log('Destinations for', selectedStore.nodeShortName, ':', destinations);
        this.destinations$.next(destinations);
        this.hasLoadedDestinations = true;
        this.hasAvailableCollectionStores = destinations.length > 0;
        this.changeDetectorRef.markForCheck();

        if (destinations.length === 0) {
          this.analyticsService.logEvent('route_unavailable_attempt', {
            origin_node_code: selectedStore.nodeCode,
            origin_node_name: selectedStore.nodeShortName,
          });
        }
      });

    this.availabilityMessage = '';
  }

  protected onCollectionStoreSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedStore = (event.option.value as PaxiNode) ?? null;
    this.collectionStoreControl.setValue(selectedStore);
    this.availabilityMessage = '';

    const originStore = this.sendingStoreControl.value;
    if (!selectedStore || !this.isMapLocation(originStore)) {
      return;
    }

    this.analyticsService.logEvent('collection_store_selected', {
      node_code: selectedStore.nodeCode,
      node_name: selectedStore.nodeShortName,
    });

    const routeParams = {
      origin_node_code: originStore.nodeCode,
      origin_node_name: originStore.nodeShortName,
      destination_node_code: selectedStore.nodeCode,
      destination_node_name: selectedStore.nodeShortName,
    };

    this.analyticsService.logEvent('route_search_success', routeParams);

    if (new Date().getHours() >= 15) {
      this.analyticsService.logEvent('eligibility_check_after_3pm', routeParams);
    }
  }

  protected clearSendingStore(): void {
    this.nodeParamErrorMessage = '';
    this.sendingStoreControl.setValue(null);
    this.collectionStoreControl.setValue(null);
    this.destinations$.next(null);
    this.hasLoadedDestinations = false;
    this.hasAvailableCollectionStores = false;
    this.loadingDestinations = false;
    this.availabilityMessage = '';
  }

  protected clearCollectionStore(): void {
    this.collectionStoreControl.setValue(null);
    this.availabilityMessage = '';
  }

  protected onAlternativeServiceClick(service: 'speed' | 'economy'): void {
    this.analyticsService.logEvent('alternative_service_click', { service });
  }

  protected readonly displayStore = (value: PaxiNode | string | null): string => {
    if (!value) {
      return '';
    }

    return this.isMapLocation(value) ? `(${value.nodeCode}) ${value.nodeShortName}` : value;
  };

  protected showSuccessState(): boolean {
    return (
      this.isMapLocation(this.sendingStoreControl.value) &&
      this.isMapLocation(this.collectionStoreControl.value)
    );
  }

  protected showErrorState(): boolean {
    return (
      this.isMapLocation(this.sendingStoreControl.value) &&
      this.hasLoadedDestinations &&
      !this.hasAvailableCollectionStores
    );
  }

  protected selectedErrorMessage(): string {
    if (
      this.isMapLocation(this.sendingStoreControl.value) &&
      this.hasLoadedDestinations &&
      !this.hasAvailableCollectionStores
    ) {
      return `Express is currently unavailable for collection at ${this.selectedSendingStoreName()}. Please select a qualifying store or use SPEED (3-5 days) or ECONOMY (7-9 days).`;
    }

    return 'This route is not available for Express delivery.';
  }

  protected selectedSendingStoreName(): string {
    return this.isMapLocation(this.sendingStoreControl.value)
      ? this.sendingStoreControl.value.nodeShortName
      : '';
  }

  protected selectedCollectionStoreName(): string {
    return this.isMapLocation(this.collectionStoreControl.value)
      ? this.collectionStoreControl.value.nodeShortName
      : '';
  }

  private filterLocations(
    locations: PaxiNode[],
    query: PaxiNode | string | null,
  ): PaxiNode[] {
    const queryText = this.isMapLocation(query) ? query.nodeShortName : (query ?? '');
    const normalizedQuery = queryText.trim().toLowerCase();
    if (!normalizedQuery) {
      return locations;
    }

    return locations.filter((location) => {
      const code = location.nodeCode?.toLowerCase() ?? '';
      const shortName = location.nodeShortName?.toLowerCase() ?? '';
      const name = location.nodeShortName?.toLowerCase() ?? '';
      return (
        code.includes(normalizedQuery) ||
        shortName.includes(normalizedQuery) ||
        name.includes(normalizedQuery)
      );
    });
  }

  private isMapLocation(value: PaxiNode | string | null): value is PaxiNode {
    return !!value && typeof value === 'object' && 'nodeCode' in value;
  }

}
